import {
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type FormEvent,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  postDemoX57StallAsk,
  postGlobalDemoPlotRecall,
  type DemoV3PlotRecallProof,
  type DemoV3PlotRecallResult,
  type DemoV3X57AskAction,
  type DemoV3X57StallAskResult,
} from "../../api/demoV3";
import { Icons } from "../thread-v4/icons";

const QUESTIONS = [
  "Is 6250 RPM interpolated or extrapolated? Show me the reason.",
  "What's the certainty at 6250 RPM? Break down the response-surface and vehicle-weight uncertainty.",
  "What happens at the heaviest weight - how does the whole curve change?",
];

export interface SelvedgeRecoveredContext {
  projectId: string;
  projectName: string;
  projectSlug: string | null;
  plotSpecId: string;
  plotTitle: string;
  artifactId: string | null;
  plotArtifactVersionId: string | null;
  analysisArtifactId: string | null;
  manifestSha256: string | null;
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** Keep the UI compatible with the project-scoped response during rollout. */
export function recoveredContext(result: DemoV3PlotRecallResult): SelvedgeRecoveredContext | null {
  const projectId = optionalString(result.project_id) ?? optionalString(result.project?.id);
  const plotSpecId = optionalString(result.plot_spec_id);
  if (!projectId || !plotSpecId) return null;
  return {
    projectId,
    projectName: optionalString(result.project_name) ?? optionalString(result.project?.name) ?? projectId,
    projectSlug: optionalString(result.project_slug) ?? optionalString(result.project?.slug),
    plotSpecId,
    plotTitle: optionalString(result.plot_title) ?? optionalString(result.plot) ?? "Recovered Plotly plot",
    artifactId: optionalString(result.artifact_id) ?? optionalString(result.recovered_artifact_id),
    plotArtifactVersionId: optionalString(result.plot_artifact_version_id),
    analysisArtifactId: optionalString(result.canonical_analysis_artifact_id),
    manifestSha256: optionalString(result.manifest_sha256),
  };
}

export function buildSelvedgePlotPath(context: SelvedgeRecoveredContext, isAnonymous: boolean): string {
  const base = isAnonymous ? "/public" : "/v5";
  const params = new URLSearchParams({
    view: "workbench",
    focus: "artifact",
    artifact: context.plotSpecId,
    plot: "large",
    graph: "1",
    lineage: "1",
    source: "selvedge",
    // Prevent workbench's default-run reconciliation from replacing the
    // recognized plot artifact used to seed the lineage DAG.
    run: "none",
  });
  // The V3 DAG is version-addressed, while the plot viewer is spec-addressed.
  // Keep the logical artifact only as a compatibility fallback for older APIs.
  const dagNodeId = context.plotArtifactVersionId ?? context.artifactId;
  if (dagNodeId) params.set("nodes", dagNodeId);
  return `${base}/${encodeURIComponent(context.projectId)}?${params.toString()}`;
}

function shortId(value: string | null, length = 12): string {
  if (!value) return "not supplied";
  return value.length > length ? `${value.slice(0, length)}…` : value;
}

function displayError(error: unknown): string {
  const message = error instanceof Error ? error.message : "Recognition failed";
  return message.replace(/^API \d+:\s*/, "");
}

function proofRows(proof: DemoV3PlotRecallProof | undefined): Array<[string, string]> {
  if (!proof) return [];
  if (typeof proof === "string") return [["Evidence", proof]];
  if (Array.isArray(proof)) {
    return proof.slice(0, 6).map((value, index) => [
      `Evidence ${index + 1}`,
      typeof value === "string" ? value : JSON.stringify(value),
    ]);
  }
  return Object.entries(proof)
    .filter(([, value]) => value !== null && value !== undefined)
    .slice(0, 6)
    .map(([key, value]) => [
      key.replace(/_/g, " "),
      typeof value === "object" ? JSON.stringify(value) : String(value),
    ]);
}

function matchLabel(result: DemoV3PlotRecallResult): string {
  if (result.match === "definitive") return "Verified match";
  if (result.match === "ambiguous") return "Ambiguous match";
  return "No match";
}

function SelvedgeAskPanel({
  context,
  isAnonymous,
}: {
  context: SelvedgeRecoveredContext;
  isAnonymous: boolean;
}) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const [question, setQuestion] = useState<string | null>(null);
  const [response, setResponse] = useState<DemoV3X57StallAskResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ask = (rawQuestion: string) => {
    const nextQuestion = rawQuestion.trim();
    if (!nextQuestion || busy) return;
    setQuestion(nextQuestion);
    setDraft("");
    setResponse(null);
    setError(null);
    setBusy(true);
    void postDemoX57StallAsk(context.projectId, {
      question: nextQuestion,
      artifact_id: context.analysisArtifactId ?? undefined,
      manifest_sha256: context.manifestSha256 ?? undefined,
      recovered_project_id: context.projectId,
      recovered_artifact_id: context.artifactId ?? undefined,
      plot_spec_id: context.plotSpecId,
      use_llm: true,
    })
      .then(setResponse)
      .catch((reason: unknown) => setError(displayError(reason)))
      .finally(() => setBusy(false));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    ask(draft);
  };

  const verifiedAction = response?.actions?.find(
    (action): action is DemoV3X57AskAction => (
      action.type === "open_artifact" &&
      action.project_id === context.projectId &&
      action.plot_spec_id === context.plotSpecId
    ),
  );

  return (
    <section className="v5-selvedge-ask" data-testid="selvedge-grounded-ask">
      <header>
        <div>
          <span className="v5-eyebrow">Evidence-grounded assistant</span>
          <h3>Ask where this value came from</h3>
        </div>
        <span className="v5-chip v5-selvedge-verified-chip"><i />artifact locked</span>
      </header>

      <div className="v5-selvedge-grounding" aria-label="Grounding context">
        <span><strong>Project</strong>{context.projectName}</span>
        <span title={context.plotSpecId}><strong>Plot spec</strong>{shortId(context.plotSpecId)}</span>
        <span title={context.manifestSha256 ?? undefined}><strong>Manifest</strong>{shortId(context.manifestSha256)}</span>
      </div>

      {!question ? (
        <div className="v5-selvedge-question-list">
          {QUESTIONS.map((suggestion) => (
            <button key={suggestion} type="button" onClick={() => ask(suggestion)} disabled={busy}>
              <Icons.Sparkle size={13} />
              <span>{suggestion}</span>
              <Icons.ChevronRight size={13} />
            </button>
          ))}
        </div>
      ) : (
        <article className="v5-selvedge-answer" aria-live="polite" data-testid="selvedge-answer">
          <strong>{question}</strong>
          {busy ? <p className="is-loading"><i />Computing evidence, then asking the model…</p> : null}
          {error ? <p className="is-error">{error}</p> : null}
          {response ? (
            <>
              <p>{response.answer}</p>
              <footer>
                <span>{response.mode === "llm_grounded"
                  ? "LLM narration"
                  : response.mode === "deterministic_repair"
                    ? "LLM checked · verified provenance"
                    : "deterministic evidence answer"}</span>
                {response.model ? <span>{response.model}</span> : null}
                <span>artifact context verified</span>
              </footer>
              {verifiedAction ? (
                <button
                  type="button"
                  className="v5-text-button"
                  data-testid="selvedge-answer-open"
                  onClick={() => navigate(buildSelvedgePlotPath(context, isAnonymous))}
                >
                  <Icons.Plot size={13} />Open exact plot from this answer
                </button>
              ) : null}
            </>
          ) : null}
        </article>
      )}

      <form className="v5-selvedge-ask-form" onSubmit={submit}>
        <input
          value={draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          placeholder="Ask about interpolation, uncertainty, weight, or lineage"
          aria-label="Ask a grounded question"
          disabled={busy}
        />
        <button className="v5-primary-button" type="submit" disabled={busy || !draft.trim()}>
          <Icons.Comments size={14} />{busy ? "Working" : "Ask"}
        </button>
      </form>
    </section>
  );
}

export function SelvedgeDemoSurface({ isAnonymous = false }: { isAnonymous?: boolean }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [result, setResult] = useState<DemoV3PlotRecallResult | null>(null);
  const [matching, setMatching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== "function") {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL?.(url);
  }, [file]);

  const context = result ? recoveredContext(result) : null;
  const verified = result?.match === "definitive" && result.verified === true && Boolean(context);

  const submitFile = (nextFile: File | null | undefined) => {
    if (!nextFile || matching) return;
    if (!nextFile.type.startsWith("image/")) {
      setError("Choose a PNG, JPEG, or other image snapshot of the plot.");
      return;
    }
    setFile(nextFile);
    setResult(null);
    setError(null);
    setMatching(true);
    void postGlobalDemoPlotRecall(nextFile)
      .then(setResult)
      .catch((reason: unknown) => setError(displayError(reason)))
      .finally(() => setMatching(false));
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setMatching(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const drop = (event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    submitFile(event.dataTransfer.files?.[0]);
  };

  const confidence = result?.confidence === null || result?.confidence === undefined
    ? null
    : `${(result.confidence * 100).toFixed(1)}%`;
  const steps = [
    { label: "Snapshot", value: file?.name ?? "waiting for image", done: Boolean(file) },
    { label: "Fingerprint", value: matching ? "searching all projects" : result ? result.match_kind ?? result.match : "not started", done: Boolean(result) },
    { label: "Project", value: context?.projectName ?? "not resolved", done: Boolean(context) },
    { label: "Plot spec", value: context ? shortId(context.plotSpecId) : "not resolved", done: Boolean(context) },
  ];

  return (
    <main className="v5-main v5-selvedge-screen v5-selvedge-real" data-testid="v5-selvedge-screen">
      <header className="v5-page-header">
        <div>
          <span className="v5-eyebrow">Selvedge</span>
          <h1>Plot recognition</h1>
          <p>Snapshot a chart in PowerPoint. Recover the exact Plotly artifact, project, lineage, and evidence in Thread.</p>
        </div>
        <div className="v5-selvedge-header-metrics" aria-label="Recognition status">
          <span><strong>{file ? "1" : "0"}</strong> snapshot</span>
          <span><strong>{matching ? "…" : confidence ?? "—"}</strong> confidence</span>
          <span><strong>{verified ? "✓" : "—"}</strong> verified</span>
        </div>
      </header>

      <section className="v5-selvedge-layout" aria-label="Selvedge recognition">
        <section className="v5-panel v5-selvedge-camera-panel">
          <header className="v5-panel-header">
            <h2><Icons.Search size={15} /> PowerPoint snapshot</h2>
            {file ? (
              <button className="v5-text-button" type="button" onClick={reset}>
                <Icons.Refresh size={13} />Scan another
              </button>
            ) : (
              <span className="v5-chip v5-selvedge-chip"><i />global project search</span>
            )}
          </header>
          <div className="v5-panel-body v5-selvedge-camera-body">
            <div className="v5-selvedge-device" data-testid="selvedge-camera">
              <div className="v5-selvedge-device-bar">
                <span />
                <strong>{file ? file.name : "Waiting for a slide snapshot"}</strong>
                <em>{matching ? "matching" : verified ? "verified" : result ? result.match : "ready"}</em>
              </div>
              <div
                className={`v5-selvedge-real-drop ${dragOver ? "is-drag-over" : ""} ${previewUrl ? "has-preview" : ""}`}
                role="button"
                tabIndex={0}
                data-testid="selvedge-upload-drop"
                onClick={() => inputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
                }}
                onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={drop}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt={`Snapshot preview: ${file?.name ?? "plot"}`} />
                ) : (
                  <div className="v5-selvedge-drop-prompt">
                    <Icons.SavedPlots size={34} />
                    <strong>Drop the plot snapshot here</strong>
                    <span>or click to choose the PNG captured from PowerPoint</span>
                    <small>Crops, resizing, and slide backgrounds are handled by perceptual matching.</small>
                  </div>
                )}
                {matching ? (
                  <div className="v5-selvedge-scanning" aria-live="polite">
                    <i />
                    <strong>Searching accessible Thread projects</strong>
                    <span>Comparing the snapshot with registered flexcomputethread plot fingerprints…</span>
                  </div>
                ) : null}
              </div>
              <input
                ref={inputRef}
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp,image/*"
                data-testid="selvedge-upload-input"
                onChange={(event) => {
                  submitFile(event.currentTarget.files?.[0]);
                  event.currentTarget.value = "";
                }}
              />
            </div>

            <ol className="v5-selvedge-steps v5-selvedge-real-steps" aria-label="Recognition progress">
              {steps.map((step) => (
                <li key={step.label} data-state={step.done ? "done" : matching ? "active" : "waiting"}>
                  <i />
                  <span>{step.label}</span>
                  <strong title={step.value}>{step.value}</strong>
                </li>
              ))}
            </ol>
            {error ? <div className="v5-selvedge-error" role="alert">{error}</div> : null}
          </div>
        </section>

        <aside className="v5-panel v5-selvedge-result-panel" data-testid="selvedge-result">
          <header className="v5-panel-header">
            <h2><Icons.Dag size={15} /> Recognition result</h2>
            {result ? <span className={`v5-chip v5-selvedge-match-chip is-${result.match}`}>{matchLabel(result)}</span> : null}
          </header>
          <div className="v5-panel-body v5-selvedge-result-body" aria-live="polite">
            {!result ? (
              <div className="v5-selvedge-empty-result">
                <Icons.Search size={30} />
                <h3>The source plot will appear here</h3>
                <p>Selvedge searches globally, then verifies the project ID and saved Plotly spec before enabling navigation or questions.</p>
              </div>
            ) : verified && context ? (
              <>
                <section className="v5-selvedge-match">
                  <span className="v5-eyebrow">Verified Thread plot</span>
                  <h3>{context.plotTitle}</h3>
                  <p>{result.match_reason ?? "The uploaded pixels matched a registered plot fingerprint and resolved to one exact saved plot."}</p>

                  <section className="v5-selvedge-found-strip" aria-label="Recovered Thread context">
                    <div><strong>Project found</strong><span title={context.projectId}>{context.projectName}</span></div>
                    <div><strong>Plot found</strong><span title={context.plotSpecId}>{shortId(context.plotSpecId)}</span></div>
                    <div><strong>Confidence</strong><span>{confidence ?? "verified"}</span></div>
                  </section>

                  <section className="v5-selvedge-proof" data-testid="selvedge-recognition-proof">
                    <header>
                      <span className="v5-eyebrow">Recognition proof</span>
                      <strong><i />IDs cross-checked</strong>
                    </header>
                    <dl>
                      <div><dt>Project ID</dt><dd title={context.projectId}>{context.projectId}</dd></div>
                      <div><dt>Plot spec ID</dt><dd title={context.plotSpecId}>{context.plotSpecId}</dd></div>
                      <div><dt>Artifact ID</dt><dd title={context.artifactId ?? undefined}>{context.artifactId ?? "not supplied"}</dd></div>
                      <div><dt>DAG node version</dt><dd title={context.plotArtifactVersionId ?? undefined}>{context.plotArtifactVersionId ?? "not supplied"}</dd></div>
                      <div><dt>Manifest</dt><dd title={context.manifestSha256 ?? undefined}>{context.manifestSha256 ?? "not supplied"}</dd></div>
                      <div><dt>Fingerprint</dt><dd>{result.match_kind ?? "verified"}{result.distance == null ? "" : ` · distance ${result.distance}`}</dd></div>
                      {proofRows(result.proof).map(([label, value]) => (
                        <div key={`${label}-${value}`}><dt>{label}</dt><dd title={value}>{value}</dd></div>
                      ))}
                    </dl>
                  </section>

                  <button
                    className="v5-primary-button v5-selvedge-open-exact"
                    type="button"
                    data-testid="selvedge-open-exact-plot"
                    onClick={() => navigate(buildSelvedgePlotPath(context, isAnonymous))}
                  >
                    <Icons.Plot size={15} />Open exact plot in Thread
                  </button>
                </section>

                <SelvedgeAskPanel context={context} isAnonymous={isAnonymous} />
              </>
            ) : (
              <section className="v5-selvedge-no-result">
                <Icons.Info size={24} />
                <h3>{matchLabel(result)}</h3>
                <p>
                  {result.match_reason ?? (result.match === "ambiguous"
                    ? "More than one registered plot is similarly close. Crop to the chart area and scan again."
                    : "No accessible Thread plot is close enough to verify. Check that the source was registered through flexcomputethread.")}
                </p>
                <button className="v5-text-button" type="button" onClick={reset}>
                  <Icons.Refresh size={13} />Try another snapshot
                </button>
              </section>
            )}
          </div>
        </aside>
      </section>
    </main>
  );
}
